import React, { useState, useRef, useCallback } from 'react'
import { Upload, X } from 'lucide-react'
import { formatFileSize } from '../utils/fileHelpers'

interface FileUploaderProps {
  onFilesSelected: (files: File[]) => void
  isUploading: boolean
  uploadProgress: number
  uploadSpeed: number
  timeRemaining: number
  currentPath: string
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesSelected,
  isUploading,
  uploadProgress,
  uploadSpeed,
  timeRemaining,
  currentPath
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])
  
  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])
  
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files)
      onFilesSelected(filesArray)
    }
  }, [onFilesSelected])
  
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files)
      onFilesSelected(filesArray)
      
      // Reset the input value so the same file can be uploaded again if needed
      e.target.value = ''
    }
  }, [onFilesSelected])
  
  const formatTimeRemaining = (seconds: number): string => {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.round(seconds % 60)
      return `${minutes}m ${remainingSeconds}s`
    } else {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return `${hours}h ${minutes}m`
    }
  }
  
  const formatSpeed = (bytesPerSecond: number): string => {
    if (bytesPerSecond < 1024) {
      return `${bytesPerSecond.toFixed(1)} B/s`
    } else if (bytesPerSecond < 1024 * 1024) {
      return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`
    } else {
      return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`
    }
  }
  
  return (
    <div className="mb-4">
      <div 
        className={`border-2 border-dashed rounded-lg p-4 transition-all ${
          isDragging 
            ? 'border-[var(--color-secondary)] bg-[var(--color-secondary)] bg-opacity-10' 
            : 'border-[var(--color-accent)] hover:border-[var(--color-secondary)]'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center">
          <Upload size={32} className="text-[var(--color-primary)] mb-1" />
          <p className="text-base font-medium mb-1">Drag and drop files here</p>
          <p className="text-[var(--color-accent)] mb-2 text-sm">or click to browse</p>
          <button
            className="btn btn-primary text-sm py-1.5"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            Select Files
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
            disabled={isUploading}
          />
        </div>
      </div>
      
      {isUploading && (
        <div className="mt-4 bg-white dark:bg-dark-background rounded-lg shadow-custom-light p-4">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-medium">Uploading files...</h3>
            <button 
              className="p-1 rounded-full hover:bg-[var(--color-background)]"
              aria-label="Cancel upload"
            >
              <X size={18} className="text-[var(--color-primary)]" />
            </button>
          </div>
          
          <div className="w-full bg-[var(--color-background)] rounded-full h-4 mb-2">
            <div 
              className="h-4 rounded-full bg-[var(--color-secondary)]"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          
          <div className="flex justify-between text-sm text-[var(--color-accent)]">
            <span>{uploadProgress.toFixed(1)}%</span>
            <span>{formatSpeed(uploadSpeed)}</span>
            <span>Remaining: {formatTimeRemaining(timeRemaining)}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default FileUploader